import { getTestCaseThread } from '../api';

import type { TestCaseThread } from '../types';
import type { ListTestCaseVersionsResponse, TestCaseVersion } from './types';

export function mapTestCaseThreadVersions(
  thread: TestCaseThread,
  testCaseId: string,
): ListTestCaseVersionsResponse {
  const normalizedTestCaseId = testCaseId.trim();

  if (!normalizedTestCaseId) {
    throw new Error('Version History requires a Test Case ID.');
  }

  const versions: TestCaseVersion[] = thread.instances.map(instance => {
    const isCurrent = thread.currentInstanceId
      ? instance.instanceId === thread.currentInstanceId
      : instance.versionNumber === thread.currentVersionNumber;

    const isApproved = thread.approvedInstanceId
      ? instance.instanceId === thread.approvedInstanceId
      : instance.status === 'APPROVED';

    return {
      versionId: instance.instanceId,
      testCaseId: normalizedTestCaseId,
      versionNumber: instance.versionNumber,
      status: isApproved ? 'APPROVED' : 'DRAFT',
      lifecycleStatus: instance.status,
      source: instance.source,
      createdAt: instance.createdAt,
      feedbackInstruction: instance.feedbackInstruction,
      isCurrent,
      fields: instance.fields,
    };
  });

  versions.sort((left, right) => {
    if (right.versionNumber !== left.versionNumber) {
      return right.versionNumber - left.versionNumber;
    }

    const rightCreatedAt = Date.parse(right.createdAt);

    const leftCreatedAt = Date.parse(left.createdAt);

    return (
      (Number.isNaN(rightCreatedAt) ? 0 : rightCreatedAt) -
      (Number.isNaN(leftCreatedAt) ? 0 : leftCreatedAt)
    );
  });

  return { versions };
}

/**
 * Returns the Test Case instance history through the Test Case thread
 * endpoint.
 *
 * The legacy Test Case versions endpoint is no longer used. Each persisted
 * Test Case instance is presented to the Version History UI as a version.
 */
export async function listTestCaseVersions(
  testCaseThreadId: string,
  testCaseId: string,
): Promise<ListTestCaseVersionsResponse> {
  const normalizedThreadId = testCaseThreadId.trim();

  const normalizedTestCaseId = testCaseId.trim();

  if (!normalizedThreadId) {
    throw new Error('Version History requires a Test Case thread ID.');
  }

  const numericThreadId = Number(normalizedThreadId);

  if (!Number.isInteger(numericThreadId) || numericThreadId <= 0) {
    throw new Error('Version History requires a valid Test Case thread ID.');
  }

  if (!normalizedTestCaseId) {
    throw new Error('Version History requires a Test Case ID.');
  }

  const thread = await getTestCaseThread(normalizedThreadId);

  return mapTestCaseThreadVersions(thread, normalizedTestCaseId);
}
